import { FC, useState, useMemo } from 'react';
import { Column, useTable } from 'react-table';
import FixtureService from '../../../services/fixture'
import HelperService from '../../../services/helper'
import useNotyf from '../../../context/useNotify';
import { League } from '../../../models/league';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEdit, faGavel, faLock, faUnlockAlt, faThumbsUp, faThumbsDown, faFutbol } from "@fortawesome/free-solid-svg-icons";
import { Meta } from '../../../models/misc';
import { StyledTable } from '../Styles';
import { Fixture, FixtureUpdateDTO } from '../../../models/fixture';
import ReactModal from '../../common/ReactModal';
import AdminFixtureService from "../../../services/admin/adminFixture"
import { useUser } from '../../../context/useUser';

interface Props {
  league: League
  fetchLeague: () => void
}

const FixtureTable: FC<Props> = ({ league, fetchLeague }) => {
  const notyf = useNotyf()
  const {user} = useUser()
  
  // API
    const updateFixture = async (id: string, dto: FixtureUpdateDTO) => {
      try {
        const updated = await AdminFixtureService.updateFixture(id, dto);
        notyf.success("Fixture updated")
      } catch (err) {
        console.log("Error updating fixture: ", err);
        HelperService.showNotfyErr(notyf, err, "Error updating fixture.");
      } finally {
        fetchLeague()
      }
    };
  
    const getFixture = async (id: string) => {
      try {
        const fixture = await FixtureService.readFixture(id);
        setFixtureToUpdate(fixture)
        console.log(fixture);
      } catch (err) {
        console.log("Error updating fixture: ", err);
        HelperService.showNotfyErr(notyf, err, "Error updating fixture.");
      }
    };

  const grabFixtureStats = async (fixtureId: string) => {
    try {
      await AdminFixtureService.grabFixtureStats(league._id, fixtureId);
      notyf.success("Stats grab job started");
    } catch (err) {
      console.log("Error starting stats grab: ", err);
      HelperService.showNotfyErr(notyf, err, "Error starting stats grab job.");
    } finally {
      setShowStatsGrabModal(false);
      setFixtureForStatsGrab(null);
      fetchLeague();
    }
  };
  
  // Methods
    const handleClickUpdate = (id: string) => {
      getFixture(id)
      setSelectedFixtureId(id);
      setShowUpdateModal(true);
    };
  
    const handleClickSettleDispute = async (id: string) => {
      const fixtureToUpdate = league.fixtures.find(fixture => fixture._id === id);
      if (!fixtureToUpdate) {
        console.log("Fixture not found.");
        return;
      }
      await updateFixture(id, {isDisputed: false});
    };
  
  const handleClickLockStatus = async (fixture: Fixture) => {
      await updateFixture(fixture._id, {isLocked: !fixture.isLocked});
    };
  
  // Filters
  const [weekFilter, setWeekFilter] = useState('');
  const [teamNameFilter, setTeamNameFilter] = useState('');
  const [dayFilter, setDayFilter] = useState('');
  const [timeFilter, setTimeFilter] = useState('');

  const filteredFixtures = useMemo(() => {
    return league.fixtures.filter(fixture => {
      const weekMatch = weekFilter ? fixture.week === parseInt(weekFilter) : true;
      const teamMatch = teamNameFilter ? 
        (fixture.home.toLowerCase().includes(teamNameFilter.toLowerCase()) || 
         fixture.away.toLowerCase().includes(teamNameFilter.toLowerCase())) : true;
      const dayMatch = dayFilter ? fixture.date.toLowerCase().includes(dayFilter.toLowerCase()) : true;
      const timeMatch = timeFilter ? fixture.time.toLowerCase().includes(timeFilter.toLowerCase()) : true;
      return weekMatch && teamMatch && dayMatch && timeMatch;
    });
  }, [league.fixtures, weekFilter, teamNameFilter, dayFilter, timeFilter]);

  // Table Data
  const columns: Column<Fixture>[] = useMemo(
    () => [
      {
        Header: 'Status',
        accessor: 'isLocked',
        Cell: ({ row }) => (
          <div className="text-center" onClick={() => handleClickLockStatus(row.original)}>
            {row.original.isLocked ? (
              <FontAwesomeIcon icon={faLock} title="Locked" className='text-danger'/>
            ) : (
              <FontAwesomeIcon icon={faUnlockAlt} title="Unlocked" className='text-success'/>
            )}
          </div>
        ),
        className: 'lock-status',
        width: 75
      },
      {
        Header: 'Match Date',
        accessor: (fixture: Fixture) => `Week ${fixture.week} - ${fixture.date}`,
        Cell: ({ cell: { value } }: { cell: { value: string } }) => (
          <div className="match-details">
            <span>{value}</span>
          </div>
        ),
      },
      {
        Header: 'Time',
        accessor: 'time',
      },
      {
        Header: 'Home Team',
        accessor: 'home',
      },
      {
        Header: 'Away Team',
        accessor: 'away',
      },
      {
        Header: 'Home Score',
        accessor: 'homeScore',
      },
      {
        Header: 'Away Score',
        accessor: 'awayScore',
      },
      {
        Header: 'Lineups',
        accessor: (fixture: Fixture) => fixture,
        Cell: ({ cell: { value: fixture } }: { cell: { value: Fixture } }) => (
          <div className="lineups-status d-flex justify-content-center align-items-center">
            <div className="d-flex align-items-center me-2">
              <span className="me-1">H:</span>
              {fixture.lineupHome.length > 0 ? (
                <FontAwesomeIcon icon={faThumbsUp} title="Home Lineup Submitted" className="text-success" />
              ) : (
                <FontAwesomeIcon icon={faThumbsDown} title="Home Lineup Not Submitted" className="text-danger" />
              )}
            </div>
            <div className="d-flex align-items-center">
              <span className="me-1">A:</span>
              {fixture.lineupAway.length > 0 ? (
                <FontAwesomeIcon icon={faThumbsUp} title="Away Lineup Submitted" className="text-success" />
              ) : (
                <FontAwesomeIcon icon={faThumbsDown} title="Away Lineup Not Submitted" className="text-danger" />
              )}
            </div>
          </div>
        ),
        width: 120,
      },
      {
        Header: 'Ext. / mins',
        accessor: 'minutesExtended',
      },
      {
        Header: 'Played',
        accessor: 'isPlayed',
        Cell: ({ row }) => (
          <div className="d-flex justify-content-center">
            {row.original.isPlayed ? (
              <FontAwesomeIcon icon={faFutbol} title="Played" className="text-success" />
            ) : (
              <FontAwesomeIcon icon={faFutbol} title="Not Played" className="text-danger" />
            )}
          </div>
        ),
        className: 'played-status',
        width: 75,
      },
      {
        Header: 'Stats',
        accessor: 'statsGrabStatus',
        Cell: ({ row }) => {
          const { icon, color, title } = getStatsGrabIcon(row.original.statsGrabStatus || 'unprocessed');
          return (
              <div
                  className="d-flex justify-content-center cursor-pointer"
                  onClick={() => handleClickStatsGrab(row.original)}
                  style={{ cursor: 'pointer' }}
              >
                <FontAwesomeIcon icon={icon} title={title} className={color} />
              </div>
          );
        },
        className: 'stats-grab-status',
        width: 75,
      },
      {
        Header: 'Actions',
        accessor: '_id',
        Cell: ({ row }) => (
          <div className="actions d-flex">
            {row.original.isDisputed && (
              <button className='btn btn-danger btn-sm me-2' onClick={() => handleClickSettleDispute(row.original._id)}>
                <FontAwesomeIcon icon={faGavel} /> Settle Dispute
              </button>
            )}
            <button className='btn btn-warning btn-sm me-2' onClick={() => handleClickUpdate(row.original._id)}>
              <FontAwesomeIcon icon={faEdit} /> Update
            </button>
          </div>
        ),
        className: 'actions',
        width: 150
      }
    ],
    [filteredFixtures]
  );
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
  } = useTable({ columns, data: filteredFixtures });

  // Pagination
  const [meta, setMeta] = useState<Meta>({
    totalPages: 0,
    page: 1,
    pageSize: 25
  });
    
  // Modals
  const [selectedFixtureId, setSelectedFixtureId] = useState<string | null>(null);

  const [showUpdateModal, setShowUpdateModal] = useState<boolean>(false);
  const [fixtureToUpdate, setFixtureToUpdate] = useState<FixtureUpdateDTO>({});
  
  const closeModals = () => {
    setShowUpdateModal(false);
    setFixtureToUpdate({});
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setFixtureToUpdate({ ...fixtureToUpdate, [name]: value });
  };

  const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { checked } = event.target;
    setFixtureToUpdate({ ...fixtureToUpdate, isPlayed: checked });
  };

  const handleSubmit = async (e: any) => {
    e.preventDefault();
    await updateFixture(selectedFixtureId!, fixtureToUpdate);
    closeModals();
  }


  const [showStatsGrabModal, setShowStatsGrabModal] = useState<boolean>(false);
  const [fixtureForStatsGrab, setFixtureForStatsGrab] = useState<Fixture | null>(null);

  const handleClickStatsGrab = (fixture: Fixture) => {
    setFixtureForStatsGrab(fixture);
    setShowStatsGrabModal(true);
  };

  const handleConfirmStatsGrab = async () => {
    if (fixtureForStatsGrab) {
      await grabFixtureStats(fixtureForStatsGrab._id);
    }
  };

  const getStatsGrabIcon = (status: string) => {
    switch (status) {
      case 'unprocessed':
        return { icon: faFutbol, color: 'text-secondary', title: 'Not Grabbed' };
      case 'processing':
        return { icon: faFutbol, color: 'text-warning', title: 'Processing...' };
      case 'processed':
        return { icon: faFutbol, color: 'text-success', title: 'Processed' };
      case 'failed':
        return { icon: faFutbol, color: 'text-danger', title: 'Failed' };
      default:
        return { icon: faFutbol, color: 'text-secondary', title: 'Unknown' };
    }
  };

  return (
      <>
      <div className='d-flex flex-column'>
      {/* Filters */}
      <div className="d-flex flex-wrap">
        <div className="col-12 col-md-3 p-2">
          <input 
            type="text" 
            className="form-control bg-dark text-white" 
            placeholder="Search by week"
            value={weekFilter}
            onChange={e => setWeekFilter(e.target.value)}
          />
        </div>
        <div className="col-12 col-md-3 p-2">
          <input 
            type="text" 
            className="form-control bg-dark text-white" 
            placeholder="Search by day"
            value={dayFilter}
            onChange={e => setDayFilter(e.target.value)}
          />
        </div>
        <div className="col-12 col-md-3 p-2">
          <input 
            type="text" 
            className="form-control bg-dark text-white" 
            placeholder="Search by time"
            value={timeFilter}
            onChange={e => setTimeFilter(e.target.value)}
          />
        </div>
        <div className="col-12 col-md-3 p-2">
          <input 
            type="text" 
            className="form-control bg-dark text-white" 
            placeholder="Search by team name"
            value={teamNameFilter}
            onChange={e => setTeamNameFilter(e.target.value)}
          />
        </div>
      </div>
        {/* Table */}
        <div className='table-responsive p-2'>
          <StyledTable {...getTableProps()}>
            <thead>
              {headerGroups.map(headerGroup => (
                <tr {...headerGroup.getHeaderGroupProps()}>
                  {headerGroup.headers.map(column => (
                    <th {...column.getHeaderProps()}>{column.render('Header')}</th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody {...getTableBodyProps()}>
              {rows.map(row => {
                prepareRow(row);
                return (
                  <tr {...row.getRowProps()}>
                    {row.cells.map(cell => {
                      return (
                        <td {...cell.getCellProps()}>{cell.render('Cell')}</td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </StyledTable>
        </div>
      </div>
          
      {/* Modals */}
      <ReactModal modalOpen={showUpdateModal} closeModal={() => closeModals()} component={
        <>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content bg-dark text-white">
              <div className="modal-header bg-dark text-white">
                <h5 className="modal-title">Update Match Details</h5>
              </div>
              <div className="modal-body">
                <form onSubmit={handleSubmit} className="row g-3">
                  <div className="col-12">
                    <strong>Fixture Information:</strong><br />
                    <span className="fw-normal">
                      Home Team: {fixtureToUpdate.home}<br />
                      Away Team: {fixtureToUpdate.away}<br />
                      Week: {fixtureToUpdate.week}<br />
                      Day: {fixtureToUpdate.date}<br />
                      Time: {fixtureToUpdate.time}
                    </span>
                  </div>
                  <div className="col-12">
                    <div className="form-check">
                      <input type="checkbox" className="form-check-input bg-dark text-white border-white" id="isPlayed" checked={fixtureToUpdate.isPlayed || false} onChange={handleCheckboxChange} />
                      <label className="form-check-label" htmlFor="isPlayed">Match Played</label>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <label htmlFor="homeScore" className="form-label">Home Score</label>
                    <input id="homeScore" name="homeScore" className="form-control bg-dark text-white border-white" value={fixtureToUpdate.homeScore} onChange={handleInputChange} />
                  </div>
                  <div className="col-md-6">
                    <label htmlFor="awayScore" className="form-label">Away Score</label>
                    <input id="awayScore" name="awayScore" className="form-control bg-dark text-white border-white" value={fixtureToUpdate.awayScore} onChange={handleInputChange} />
                  </div>
                  <div className="col-12">
                    <label htmlFor="minutesExtended" className="form-label">Minutes Extended</label>
                    <input id="minutesExtended" name="minutesExtended" className="form-control bg-dark text-white border-white" value={fixtureToUpdate.minutesExtended} onChange={handleInputChange} />
                  </div>
                </form>
              </div>
              <div className="modal-footer bg-dark text-white">
                <button type="button" className="btn btn-outline-light" onClick={() => closeModals()}>Cancel</button>
                <button type="submit" className="btn btn-primary" onClick={handleSubmit}>Update</button>
              </div>
            </div>
          </div>
        </>
      } />

      <ReactModal modalOpen={showStatsGrabModal} closeModal={() => setShowStatsGrabModal(false)} component={
        <>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content bg-dark text-white">
              <div className="modal-header bg-dark text-white">
                <h5 className="modal-title">Start Stats Grab Job</h5>
              </div>
              <div className="modal-body">
                <p>Are you sure you want to start the stats grab job for this fixture?</p>
                {fixtureForStatsGrab && (
                    <div className="mt-3">
                      <strong>Fixture Details:</strong><br />
                      <span className="fw-normal">
              Home: {fixtureForStatsGrab.home}<br />
              Away: {fixtureForStatsGrab.away}<br />
              Week: {fixtureForStatsGrab.week}<br />
              Status: {fixtureForStatsGrab.statsGrabStatus || 'unprocessed'}
            </span>
                    </div>
                )}
              </div>
              <div className="modal-footer bg-dark text-white">
                <button type="button" className="btn btn-outline-light" onClick={() => setShowStatsGrabModal(false)}>Cancel</button>
                <button type="button" className="btn btn-primary" onClick={handleConfirmStatsGrab}>Start Job</button>
              </div>
            </div>
          </div>
        </>
      } />
    </>
  );
};

export default FixtureTable;
